import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import * as goalApi from '../services/goal-tree-api.js';
import type { GoalNode } from '../services/goal-tree-api.js';
import * as companyApi from '../services/company-api.js';
import '../components/copilot/cp-goal-copilot.js';

interface CompanyLite { id: number; name: string }

/**
 * Goal-Tree-Editor (compass-new Layer 1). Säulen/Objectives je Firma sehen + bearbeiten,
 * inkl. Hard-Constraints (Decision-Gate) und Keywords (Relevanz). Tuning-Hebel.
 * Constraints/Keywords werden als JSON editiert (flexibel + ehrlich, bis strukturierte Felder folgen).
 */
@customElement('cp-page-goals')
export class CpPageGoals extends LitElement {
  createRenderRoot() { return this; }

  @state() private _companies: CompanyLite[] = [];
  @state() private _companyId = 0;
  @state() private _nodes: GoalNode[] = [];
  @state() private _loading = false;
  @state() private _error = '';

  override connectedCallback(): void {
    super.connectedCallback();
    void this._init();
  }

  private async _init(): Promise<void> {
    try {
      this._companies = (await companyApi.listCompanies()) as unknown as CompanyLite[];
      if (this._companies.length) { this._companyId = this._companies[0].id; await this._load(); }
    } catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
  }

  private async _load(): Promise<void> {
    if (!this._companyId) return;
    this._loading = true; this._error = '';
    try { this._nodes = await goalApi.listGoalTree(this._companyId); }
    catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
    finally { this._loading = false; }
  }

  private _onCompany(e: Event): void {
    this._companyId = Number((e.target as HTMLSelectElement).value);
    void this._load();
  }

  private _val(id: number, field: string): string {
    const el = this.querySelector(`#n${id}-${field}`) as HTMLInputElement | HTMLTextAreaElement | null;
    return el ? el.value : '';
  }

  private async _save(id: number): Promise<void> {
    try {
      const hard = JSON.parse(this._val(id, 'hard') || '{}') as Record<string, unknown>;
      const soft = JSON.parse(this._val(id, 'soft') || '{}') as Record<string, unknown>;

      // Threshold inputs are authoritative for soft_signals.thresholds
      const relStr = this._val(id, 'th-relevant').trim();
      const uncStr = this._val(id, 'th-unclear').trim();
      const relVal = relStr ? parseFloat(relStr) : NaN;
      const uncVal = uncStr ? parseFloat(uncStr) : NaN;
      delete soft.thresholds;
      if (!isNaN(relVal) || !isNaN(uncVal)) {
        soft.thresholds = {
          ...(!isNaN(relVal) ? { relevant: relVal } : {}),
          ...(!isNaN(uncVal) ? { unclear: uncVal } : {}),
        };
      }

      await goalApi.updateGoalNode(id, {
        text: this._val(id, 'text'),
        weight: Number(this._val(id, 'weight')) || 1,
        hardConstraints: hard,
        softSignals: soft,
        relevanceDescription: this._val(id, 'relevance'),
      });
      await this._load();
    } catch (e) { this._error = e instanceof Error ? e.message : 'JSON ungültig?'; }
  }

  private async _add(type: GoalNode['type'], parentId: number | null): Promise<void> {
    await goalApi.createGoalNode({ companyId: this._companyId, parentId, type, text: type === 'pillar' ? 'Neue Säule' : 'Neues Objective', weight: 1 });
    await this._load();
  }

  private async _del(id: number): Promise<void> {
    if (!confirm('Knoten + alle Unterknoten löschen?')) return;
    await goalApi.deleteGoalNode(id);
    await this._load();
  }

  private _nodeForm(n: GoalNode) {
    const thresholds = (n.softSignals as { thresholds?: { relevant?: number; unclear?: number } }).thresholds;
    return html`
      <div class="g-node g-${n.type}">
        <div class="g-row">
          <span class="g-type">${n.type}</span>
          <input id="n${n.id}-text" .value=${n.text} class="g-text" />
          <label class="g-w">w<input id="n${n.id}-weight" type="number" step="0.1" .value=${String(n.weight)} /></label>
          <button @click=${() => void this._save(n.id)}>Speichern</button>
          <button class="g-del" @click=${() => void this._del(n.id)}>Löschen</button>
        </div>
        <div class="g-embed">
          <label class="g-embed-desc">Relevanz-Beschreibung
            <textarea id="n${n.id}-relevance" rows="2" placeholder="Wofür ist dieser Knoten relevant? (Embedding-Basis)">${n.relevanceDescription}</textarea>
          </label>
          <div class="g-thresholds">
            <label>Schwelle Relevant<input id="n${n.id}-th-relevant" type="number" step="0.01" min="0" max="1" placeholder="0.78" .value=${thresholds?.relevant != null ? String(thresholds.relevant) : ''} /></label>
            <label>Schwelle Unklar<input id="n${n.id}-th-unclear" type="number" step="0.01" min="0" max="1" placeholder="0.68" .value=${thresholds?.unclear != null ? String(thresholds.unclear) : ''} /></label>
          </div>
        </div>
        <div class="g-json">
          <label>hard_constraints<textarea id="n${n.id}-hard" rows="2">${JSON.stringify(n.hardConstraints)}</textarea></label>
          <label>soft_signals<textarea id="n${n.id}-soft" rows="2">${JSON.stringify(n.softSignals)}</textarea></label>
        </div>
      </div>
    `;
  }

  override render() {
    const vision = this._nodes.find((n) => n.type === 'vision');
    const pillars = this._nodes.filter((n) => n.type === 'pillar');
    return html`
      <style>
        .goals-page { padding: 1.5rem; max-width: 1400px; margin: 0 auto; display: grid; grid-template-columns: 1fr 380px; gap: 1.5rem; align-items: start; }
        .goals-copilot { position: sticky; top: 1rem; height: calc(100vh - 6rem); }
        @media (max-width: 1000px) { .goals-page { grid-template-columns: 1fr; } .goals-copilot { position: static; height: 520px; } }
        .goals-wrap { font-family: var(--font-family-sans); color: var(--color-text-primary); min-width: 0; }
        .goals-head { display: flex; align-items: baseline; gap: 1rem; margin-bottom: 1rem; }
        .goals-head h1 { font-size: 1.4rem; margin: 0; color: var(--color-text-primary); }
        select { padding: .35rem .5rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border: 1px solid var(--color-border); border-radius: 6px; }
        .g-node { border: 1px solid var(--color-border); border-radius: 8px; padding: .6rem .8rem; margin: .5rem 0; background: var(--color-bg-secondary); }
        .g-vision { background: var(--color-bg-tertiary); }
        .g-pillar { margin-left: 0; border-left: 3px solid var(--color-accent-primary); }
        .g-objective { margin-left: 1.5rem; border-left: 3px solid var(--color-accent-success); }
        .g-row { display: flex; align-items: center; gap: .5rem; }
        .g-type { font-size: .7rem; text-transform: uppercase; color: var(--color-text-muted); width: 70px; }
        .g-text { flex: 1; padding: .35rem .5rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border: 1px solid var(--color-border); border-radius: 6px; }
        .g-w { color: var(--color-text-secondary); font-size: .8rem; }
        .g-w input { width: 56px; margin-left: .3rem; padding: .3rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border: 1px solid var(--color-border); border-radius: 6px; }
        .g-del { color: var(--color-accent-error); border-color: var(--color-accent-error); }
        .g-embed { display: flex; gap: 1rem; margin-top: .4rem; align-items: flex-start; }
        .g-embed-desc { flex: 1; font-size: .75rem; color: var(--color-text-secondary); display: flex; flex-direction: column; gap: .25rem; }
        .g-embed-desc textarea { font-size: .8rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border: 1px solid var(--color-border); border-radius: 6px; padding: .35rem; resize: vertical; }
        .g-thresholds { display: flex; flex-direction: column; gap: .4rem; min-width: 160px; }
        .g-thresholds label { font-size: .75rem; color: var(--color-text-secondary); display: flex; flex-direction: column; gap: .2rem; }
        .g-thresholds input { width: 100%; padding: .3rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border: 1px solid var(--color-border); border-radius: 6px; font-size: .8rem; }
        .g-json { display: flex; gap: 1rem; margin-top: .4rem; }
        .g-json label { flex: 1; font-size: .75rem; color: var(--color-text-secondary); display: flex; flex-direction: column; gap: .25rem; }
        .g-json textarea { font-family: var(--font-family-mono); font-size: .8rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border: 1px solid var(--color-border); border-radius: 6px; padding: .35rem; }
        button { cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 6px; padding: .35rem .7rem; }
        button:hover { border-color: var(--color-border-hover); background: var(--color-bg-hover); }
        .g-add { margin: .3rem 0 1rem; }
      </style>
      <div class="goals-page">
      <div class="goals-wrap">
        <div class="goals-head">
          <h1>Ziele / Goal-Tree</h1>
          <select @change=${this._onCompany}>
            ${this._companies.map((c) => html`<option value=${c.id} ?selected=${c.id === this._companyId}>${c.name}</option>`)}
          </select>
          <button class="goals-head-refresh" @click=${() => void this._load()}>↻</button>
        </div>
        ${this._error ? html`<p style="color:#c33">${this._error}</p>` : nothing}
        ${this._loading ? html`<p>Lädt…</p>` : nothing}

        ${vision
          ? this._nodeForm(vision)
          : html`<button @click=${() => void this._add('vision', null)}>Vision anlegen</button>`}

        ${vision ? html`
          <div class="g-add"><button @click=${() => void this._add('pillar', vision.id)}>+ Säule</button></div>
          ${pillars.map((p) => html`
            ${this._nodeForm(p)}
            ${this._nodes.filter((o) => o.parentId === p.id).map((o) => this._nodeForm(o))}
            <div class="g-add"><button @click=${() => void this._add('objective', p.id)}>+ Objective unter „${p.text}“</button></div>
          `)}
        ` : nothing}
      </div>
      <aside class="goals-copilot">
        ${this._companyId
          ? html`<cp-goal-copilot .companyId=${this._companyId} @tree-changed=${() => void this._load()}></cp-goal-copilot>`
          : nothing}
      </aside>
      </div>
    `;
  }
}
